import {readonly, ref} from 'vue'
import {useApi} from '~/composables/api/useApi'
import {buildApiParams} from '~/utils/api/buildApiParams'
import {deserializeResponse} from '~/utils/api/deserializeResponse'

export const useApiResource = (resourceName, options = {}) => {
  const {
    deserialize = true,
    methods: methodOptions = {}
  } = options

  const { makeRequest } = useApi()
  const loading = ref(false)
  const error = ref(null)

  const enabledMethods = {
    list: methodOptions.list !== false,
    getById: methodOptions.getById !== false,
    create: methodOptions.create !== false,
    update: methodOptions.update !== false,
    delete: methodOptions.delete !== false
  }

  const withLoadingState = async (apiCall) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall()
    } catch (e) {
      error.value = e
      throw e
    } finally {
      loading.value = false
    }
  }

  const maybeDeserialize = async (response) => {
    if (!deserialize) {
      return response
    }
      return await deserializeResponse(response)
  }

  const methods = {}

  if (enabledMethods.list) {
    methods.list = async (params = {}) => {
      return withLoadingState(async () => {
        const apiParams = buildApiParams(params)
        const response = await makeRequest({
          url: `/${resourceName}`,
          method: 'GET',
          params: apiParams
        })
        const deserialized = await maybeDeserialize(response)
        return { data: deserialized, meta: response.meta }
      })
    }
  }

  if (enabledMethods.getById) {
    methods.getById = async (id, options = {}) => {
      return withLoadingState(async () => {
        const params = buildApiParams({ include: options.include })
        const response = await makeRequest({
          url: `/${resourceName}/${id}`,
          method: 'GET',
          params
        })
        const deserialized = await maybeDeserialize(response)
        return { data: deserialized }
      })
    }
  }

  if (enabledMethods.create) {
    methods.create = async (data) => {
      return withLoadingState(async () => {
        const response = await makeRequest({
          url: `/${resourceName}`,
          method: 'POST',
          data
        })
        // W21: mismo shape que list/getById/customEndpoint ({data: entidad
        // deserializada}) — antes se devolvía el envelope JSON:API crudo, una
        // trampa para el consumidor (los callers viejos que leían
        // `response.data.id` siguen funcionando: la entidad deserializada
        // también lleva `id`)
        const deserialized = await maybeDeserialize(response)
        return { data: deserialized, meta: response?.meta }
      })
    }
  }

  if (enabledMethods.update) {
    methods.update = async (id, data) => {
      return withLoadingState(async () => {
        const response = await makeRequest({
          url: `/${resourceName}/${id}`,
          method: 'PATCH',
          data
        })
        const deserialized = await maybeDeserialize(response)
        return { data: deserialized, meta: response?.meta }
      })
    }
  }

  if (enabledMethods.delete) {
    // W21: delete no devuelve cuerpo (204) — queda en undefined, coherente
    // con el shape de arriba (nada que deserializar)
    methods.delete = async (id) => {
      return withLoadingState(async () => {
        await makeRequest({
          url: `/${resourceName}/${id}`,
          method: 'DELETE'
        })
      })
    }
  }

  const customEndpoint = (configFn) => {
    return async (...args) => {
      return withLoadingState(async () => {
        const config = configFn(...args)
        const { url, method = 'GET', params, data, _skipAuth, deserialize, customHeaders } = config
        const response = await makeRequest({ url, method, params, data, _skipAuth, customHeaders })
        if (deserialize === false) {
          return response
        }
        const deserialized = await maybeDeserialize(response)
        return { data: deserialized, meta: response?.meta }
      })
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    ...methods,
    customEndpoint
  }
}
